/**
 * Organization roles and the role → scope matrix (org roles step 2,
 * docs/USER_ROLES_AND_PERMISSIONS.md).
 *
 * ONE matrix per layer, asserted equal: this file is the TS copy; the SQL copy
 * is `public.org_role_has_scope(role, scope)`
 * (last restated in `supabase/migrations/20261013120000_level_up.sql`),
 * and `supabase/tests/manual/org_roles.sql` checks all 4 × 5 cells against
 * the same table as `orgRoles.test.ts`.
 *
 *   scope \ role       admin  member  accountant  project_manager
 *   accounting           ✓      ✓        ✓              –
 *   accounting_admin     ✓      –        ✓              –
 *   admin                ✓      –        –              –
 *   surveys              ✓      ✓        ✓              ✓
 *   level_up             ✓      ✓        –              ✓
 *
 * `surveys` is held by every role today (behaviour unchanged); it exists so a
 * future role can be given or denied Surveys as one switch (docs/SURVEYS.md).
 *
 * `level_up` is every role but accountant (docs/LEVEL_UP.md): an accountant
 * keeps the books and sees no Level Up assessment data.
 *
 * `admin` is a scope of the MATRIX only (org roles step 3): it lets the SQL
 * layer derive "org admin" from the one map (`user_is_organization_admin`,
 * `current_user_org_ids('admin')`) instead of a second role literal. It is NOT
 * a guard scope: org administration (team, invitations, org settings) is
 * checked with `requireOrgAdmin` / `assertOrgAdminRole` / `isAdmin`, and the
 * scope guards take {@link AreaScope} (which excludes it) so
 * `requireOrgScope('admin')` cannot become a second admin check.
 */
/** Mirrors the `organization_members_role_check` / `organization_invitations.valid_role` CHECKs. */
export const ORG_ROLES = ["admin", "member", "accountant", "project_manager"];
/**
 * `accounting` — ledger, AP/AR, expenses, bank, VAT, budgets, Ré bookkeeper,
 * financial reports, dashboard money, hourly rates, receipts.
 * `accounting_admin` — tax settings, the default tax rate, the Audit Log.
 * `admin` — org administration; carried by role `admin` only (see the header).
 * `surveys` — engagement surveys (build, share, results, staff responses).
 * `level_up` — Level Up assessments (authoring, cycles, scorecards); not accountant.
 */
export const ORG_SCOPES = ["accounting", "accounting_admin", "admin", "surveys", "level_up"];
const ROLE_SCOPES = {
    admin: ["accounting", "accounting_admin", "admin", "surveys", "level_up"],
    member: ["accounting", "surveys", "level_up"],
    accountant: ["accounting", "accounting_admin", "surveys"],
    project_manager: ["surveys", "level_up"],
};
export function isOrgRole(value) {
    return typeof value === "string" && ORG_ROLES.includes(value);
}
/** Does `role` carry `scope`? An unknown / missing role carries nothing (fail closed). */
export function orgRoleHasScope(role, scope) {
    if (!isOrgRole(role))
        return false;
    return ROLE_SCOPES[role].includes(scope);
}
/** i18n keys (web `common` namespace, `team.roles.*`) for each role's display label. */
export const ORG_ROLE_LABEL_KEYS = {
    admin: "team.roles.admin",
    member: "team.roles.member",
    accountant: "team.roles.accountant",
    project_manager: "team.roles.projectManager",
};
//# sourceMappingURL=orgRoles.js.map